import { Location } from '@/assets/icons/filters';
import { Flex, Icon, useDisclosure, Box } from '@chakra-ui/react';
import { Edit05 } from '@kula-ai/icons/outline/Edit05';
import { Trash03 } from '@kula-ai/icons/outline/Trash03';
import { BiTimeFive } from 'react-icons/bi';
import { LuCalendarPlus } from 'react-icons/lu';
import { useInterviewStageManager } from '../stage-manager';
import { useShowToast } from '@/hooks';
import { useDeleteActivityMutation } from '@/store/query/interview-plan';
import { atsJobCreateApiActions } from '@/store/atsJobCreate';
import { useDispatch } from 'react-redux';
import { locationValuemap } from './activity-sections/schedule-interview/helpers';
import { TruncatedTextWithTooltip } from '@/ui/TooltipText';
import { KText } from '@/ui-library/components/text';
import { ActivityPopover } from './activity-sections/ActivityPopover';
import { ModalWithForm } from '@/components/ats';
import * as Yup from 'yup';
import { Mail01 } from '@kula-ai/icons/outline/Mail01';
import { Eye } from '@kula-ai/icons/outline/Eye';
import { Assessment, SurveyV2 } from '@/assets/icons/interview-plan';
import { Hackerrank, Hackerearth, CriteriaCorp } from '@/assets/icons/ats';
import { useIntegrations } from '@/hooks/use-integrations';
import SvgDragAndDrop from '@/assets/icons/table/DragAndDrop';
import { Draggable } from 'react-beautiful-dnd';

const interviewTypeValueMap = {
  one_on_one: '1 on 1',
  panel: 'Panel',
};

const activityIconMap = {
  interview: {
    icon: LuCalendarPlus,
  },
  email: {
    icon: Mail01,
  },
  review: {
    icon: Eye,
  },
  assessment: {
    icon: Assessment,
  },
  hackerrank: {
    icon: Hackerrank,
    iconProps: {
      borderRadius: '6px',
    },
  },
  hackerrank_interview: {
    icon: Hackerrank,
    iconProps: {
      borderRadius: '6px',
    },
  },
  hackerearth_assessment: {
    icon: Hackerearth,
    iconProps: {
      borderRadius: '6px',
    },
  },
  criteriacorp_assessment: {
    icon: CriteriaCorp,
    iconProps: {
      borderRadius: '6px',
    },
  },
  survey: {
    icon: SurveyV2,
    iconProps: {
      borderRadius: '6px',
    },
  },
};

// External location icon mapping for interviews
const externalLocationIconMap = {
  hackerrank: {
    icon: Hackerrank,
    iconProps: {
      borderRadius: '6px',
    },
  },
  hackerearth: {
    icon: Hackerearth,
    iconProps: {
      borderRadius: '6px',
    },
  },
};

const getActivityIcon = (activity, location) => {
  if (
    activity?.kind === 'interview' &&
    location &&
    externalLocationIconMap[location]
  ) {
    return externalLocationIconMap[location];
  }

  // Fallback to activity type icon
  return activityIconMap?.[activity?.kind] || activityIconMap.interview;
};

const IconText = ({ icon = BiTimeFive, text = '30m', ...props }) => {
  return (
    <Flex gap="5px" alignItems="center" width="50px" {...props}>
      <Icon as={icon} width="14px" height="14px" />
      <TruncatedTextWithTooltip size="sm-medium" text={text} />
    </Flex>
  );
};

export const ActivityCard = ({
  activity = {},
  stage = {},
  disableAction = false,
  disableDrag = false,
  index,
  isFixedStage = false,
}) => {
  const {
    activityDisclosure: { onOpen },
    setActivityAction,
    setSelectedActivity,
    isSettings,
    jobId,
    refresh,
    canEditJob,
  } = useInterviewStageManager();
  const stageId = stage?.id;
  const toast = useShowToast();
  const [deleteActivity, { isLoading: isDeleteLoading }] =
    useDeleteActivityMutation();
  const { isHackerRankEnabled, isHackerEarthEnabled, isCriteriaCorpEnabled } =
    useIntegrations();
  const dispatch = useDispatch();
  const {
    isOpen: isDeleteOpen,
    onOpen: onDeleteOpen,
    onClose: onDeleteClose,
  } = useDisclosure();

  const {
    label = '',
    duration,
    interview_type,
    location,
    office = {},
  } = activity;

  if (activity?.kind === 'hackerrank' && !isHackerRankEnabled) {
    return null;
  }
  if (activity?.kind === 'hackerearth_assessment' && !isHackerEarthEnabled) {
    return null;
  }
  if (activity?.kind === 'criteriacorp_assessment' && !isCriteriaCorpEnabled) {
    return null;
  }
  const officeName = office?.id ? `(${office?.name || office?.location})` : '';
  const locationName = locationValuemap?.[location]
    ? `${locationValuemap?.[location]} ${officeName}`
    : 'Unspecified';

  const handleDelete = async () => {
    const result = await deleteActivity({
      payload: { jobId, stageId: stage?.id, activityId: activity?.id },
    });

    if (!result?.error?.length) {
      toast({ title: `Activity deleted successfully` });
      dispatch(
        atsJobCreateApiActions('getInterviewPlan', {
          pathParams: { jobId: jobId },
          isBackground: true,
        })
      );
      refresh();
    } else {
      toast({ title: `Activity deletion failed`, status: 'error' });
    }
    onDeleteClose();
  };

  const handleDeleteClick = () => {
    if (isSettings) {
      onDeleteOpen();
      return;
    }
    handleDelete();
  };

  const deleteFormFields = [
    {
      name: 'deleteInput',
      type: 'input',
      isRequired: true,
      placeholder: 'Type "DELETE"',
      errorMessage: 'Confirmation message is required',
      validation: Yup.string()
        .oneOf(['DELETE'], `Confirmation message doesn't match`)
        .required('Confirmation message is required'),
    },
  ];

  return (
    <>
      {!disableDrag && (
        <Draggable
          key={activity?.id}
          draggableId={`${activity?.id}`}
          index={index}
          isDragDisabled={!canEditJob}
        >
          {(provided, snapshot) => (
            <Flex
              ref={provided.innerRef}
              {...provided.draggableProps}
              h="50px"
              w="100%"
              maxW="100%"
              background={isFixedStage ? 'base-white' : 'secondary-25'}
              border="1px solid"
              borderColor="border-color"
              borderRadius="sm"
              alignItems="center"
              pl="12px"
              pr="12px"
              justifyContent="space-between"
              data-test-id="card-activity"
              gap="6px"
              style={{
                ...provided.draggableProps.style,
              }}
            >
              {/* Drag Handle - Only visible when canEditJob */}
              {canEditJob && (
                <Box
                  {...provided.dragHandleProps}
                  cursor={snapshot.isDragging ? 'grabbing' : 'grab'}
                  display="flex"
                  alignItems="center"
                >
                  <Icon
                    as={SvgDragAndDrop}
                    width="14px"
                    height="14px"
                    color="gray.500"
                  />
                </Box>
              )}

              <Flex gap="12px" alignItems="center" flex="1" minW="0">
                <Icon
                  as={getActivityIcon(activity, location)?.icon}
                  width="20px"
                  height="20px"
                  color="ats-primary"
                  {...(getActivityIcon(activity, location)?.iconProps || {})}
                />
                <Flex flex="1" minW="0">
                  <KText
                    size="sm-semi-bold"
                    maxW="100%"
                    enableTooltip={true}
                    tooltipText={label}
                    isTruncated
                  >
                    {label}
                  </KText>
                </Flex>
                {activity?.kind === 'interview' && (
                  <TruncatedTextWithTooltip
                    bg="primary-100"
                    color="primary-500"
                    fontSize="sm"
                    fontWeight="semiBold"
                    borderRadius="xs"
                    p="2px 8px"
                    w="fit-content"
                    maxW="80px"
                    text={
                      interviewTypeValueMap?.[interview_type] ||
                      'Not configured'
                    }
                  />
                )}
              </Flex>
              {activity?.kind === 'interview' && (
                <Flex gap="12px" alignItems="center" flexShrink={0} px="6px">
                  <ActivityPopover activity={activity} />
                  <IconText
                    icon={BiTimeFive}
                    text={`${duration ? `${duration}m` : '-'}`}
                  />
                  <IconText
                    icon={Location}
                    text={locationName}
                    maxW="140px"
                    width="fit-content"
                  />
                </Flex>
              )}
              {!disableAction && canEditJob && (
                <Flex gap="6px" alignItems="center" flexShrink={0}>
                  <Icon
                    as={Edit05}
                    cursor="pointer"
                    onClick={() => {
                      setSelectedActivity(activity?.kind);
                      setActivityAction({
                        type: 'update',
                        activity,
                        stageId: stageId,
                        stage,
                      });
                      onOpen();
                    }}
                    data-test-id="edit-activity-button"
                  />
                  <Icon
                    as={Trash03}
                    cursor="pointer"
                    onClick={handleDeleteClick}
                    data-test-id="delete-activity-button"
                  />
                </Flex>
              )}
            </Flex>
          )}
        </Draggable>
      )}
      {disableDrag && (
        <Flex
          h="50px"
          w="100%"
          maxW="100%"
          background="#F4F4FB4F"
          border="1px solid"
          borderColor="border-color"
          borderRadius="sm"
          alignItems="center"
          pl="xl"
          pr="xl"
          justifyContent="space-between"
          data-test-id="card-activity"
          gap="12px"
        >
          <Flex gap="12px" alignItems="center" flex="1" minW="0">
            <Icon
              as={getActivityIcon(activity, location)?.icon}
              width="20px"
              height="20px"
              color="ats-primary"
              {...(getActivityIcon(activity, location)?.iconProps || {})}
            />
            <Flex flex="1" minW="0">
              <KText
                size="sm-semi-bold"
                maxW="100%"
                enableTooltip={true}
                tooltipText={label}
                isTruncated
              >
                {label}
              </KText>
            </Flex>
            {activity?.kind === 'interview' && (
              <TruncatedTextWithTooltip
                bg="ats-tertiary"
                color="ats-primary"
                fontSize="sm"
                fontWeight="semiBold"
                borderRadius="xs"
                p="2px 8px"
                w="fit-content"
                maxW="80px"
                text={
                  interviewTypeValueMap?.[interview_type] || 'Not configured'
                }
              />
            )}
          </Flex>
          {activity?.kind === 'interview' && (
            <Flex gap="12px" alignItems="center" flexShrink={0}>
              <ActivityPopover activity={activity} />
              <IconText
                icon={BiTimeFive}
                text={`${duration ? `${duration}m` : '-'}`}
              />
              <IconText
                icon={Location}
                text={locationName}
                maxW="140px"
                width="fit-content"
              />
            </Flex>
          )}
          {!disableAction && canEditJob && (
            <Flex gap="6px" alignItems="center" flexShrink={0}>
              <Icon
                as={Edit05}
                cursor="pointer"
                onClick={() => {
                  setSelectedActivity(activity?.kind);
                  setActivityAction({
                    type: 'update',
                    activity,
                    stageId: stageId,
                    stage,
                  });
                  onOpen();
                }}
                data-test-id="edit-activity-button"
              />
              <Icon
                as={Trash03}
                cursor="pointer"
                onClick={handleDeleteClick}
                data-test-id="delete-activity-button"
              />
            </Flex>
          )}
        </Flex>
      )}
      {isDeleteOpen && (
        <ModalWithForm
          isOpen={isDeleteOpen}
          onClose={onDeleteClose}
          headerText="Are you sure you want to delete this activity?"
          subHeaderText="Deleting a activity means it won’t be executed for the future candidates."
          primaryCtaText="Yes, Delete"
          handleSubmit={handleDelete}
          formConfig={deleteFormFields}
          isCtaLoading={isDeleteLoading}
        />
      )}
    </>
  );
};
